import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, Clock, MapPin } from "lucide-react";
import { MaskedLines } from "@/components/motion/Reveal";

/*
 * Visuel hero : image d'illustration libre de droits (Unsplash).
 * Pour remplacer par une vraie photo de chantier : déposer le fichier dans
 * frontend/public/images/hero.jpg et remplacer HERO_IMAGE par "/images/hero.jpg".
 */
const HERO_IMAGE = "https://images.unsplash.com/photo-1692119442094-45806e38a35e?crop=entropy&cs=srgb&fm=jpg&q=80&w=1800";

export const Hero = () => {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, reduce ? 0 : 120]);
  const scale = useTransform(scrollY, [0, 600], [1, reduce ? 1 : 1.12]);

  return (
    <section className="relative bg-slate text-white overflow-hidden min-h-[92vh] flex items-end grain" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img src={HERO_IMAGE} alt="Façade d'immeuble en cours de rénovation — visuel d'illustration" className="w-full h-full object-cover" width="1800" height="1200" fetchPriority="high" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-slate via-slate/85 to-slate/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate via-transparent to-transparent" />

      <motion.div
        className="absolute right-[-8%] top-[8%] w-[46vw] max-w-[640px] aspect-square rounded-full border-[3px] border-copper/40 hidden lg:block"
        initial={reduce ? false : { rotate: -30, opacity: 0 }}
        animate={{ rotate: 0, opacity: 1 }}
        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        aria-hidden="true"
      >
        <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-copper" />
      </motion.div>

      <div className="container-x relative z-10 pb-20 pt-40 w-full">
        <motion.p
          className="eyebrow mb-6"
          initial={reduce ? false : { opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          Entreprise artisanale · Nogent-sur-Marne (94)
        </motion.p>
        <h1 className="h-display !text-white text-[2.7rem] sm:text-6xl lg:text-[5.5rem] max-w-5xl" data-testid="hero-title">
          <MaskedLines lines={["O'RIZON 360 —", "Rénovation et entretien", "d'immeubles en Île-de-France"]} delay={0.2} />
        </h1>
        <motion.p
          className="mt-8 max-w-2xl text-base sm:text-lg text-white/80 leading-relaxed"
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          data-testid="hero-subtitle"
        >
          Maçonnerie, étanchéité, isolation, équipements techniques : un interlocuteur unique pour vos travaux, du diagnostic à la finition.
        </motion.p>
        <motion.div
          className="mt-10 flex flex-col sm:flex-row gap-4"
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
        >
          <Link to="/contact" className="btn-primary" data-testid="hero-devis-cta-button">Demander un devis gratuit <ArrowRight size={18} /></Link>
          <Link to="/services" className="btn-ghost-light" data-testid="hero-services-button">Voir nos services <ArrowUpRight size={18} /></Link>
        </motion.div>

        <motion.ul
          className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden max-w-3xl backdrop-blur"
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
        >
          {[
            { icon: Clock, t: "Devis sous 48 h", s: "ouvrées, gratuit" },
            { icon: ShieldCheck, t: "Décennale & RC Pro", s: "attestations sur demande" },
            { icon: MapPin, t: "Toute l'Île-de-France", s: "siège à Nogent-sur-Marne" },
          ].map(({ icon: Icon, t, s }) => (
            <li key={t} className="bg-slate/70 px-5 py-4 flex items-center gap-3">
              <Icon className="text-copper shrink-0" size={22} />
              <div><p className="font-heading font-bold uppercase text-lg leading-none">{t}</p><p className="text-xs text-white/60 mt-1">{s}</p></div>
            </li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
};
